import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cloneElement, forwardRef, isValidElement, useMemo } from "react";
import { mergeProps, useFocusRing, usePress } from "react-aria";
import { tv } from "tailwind-variants";

import { useDomRef } from "../../hooks/useDomRef";
import { cn } from "../../utils/cn";

import type { HTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

// Export color variants for use in other components
export type TagColorVariant = "neutral" | "accent" | "warning" | "success" | "danger" | "purple";

const tag = tv({
  slots: {
    base: [
      "text-xs",
      "text-primary",
      "inline-flex",
      "items-center",
      "gap-x-1",
      "whitespace-nowrap",
      "transition-colors",
      "focus-visible:outline-none",
      "focus-visible:ring-offset-0",
      "focus-visible:ring-1",
      "focus-visible:ring-blue-300",
      "box-border border border-solid",
    ],
    content: "text-inherit flex px-1",
    removeButton: [
      "text-xs",
      "text-current",
      "cursor-pointer",
      "appearance-none",
      "outline-none",
      "select-none",
      "opacity-60",
      "transition-opacity",
      "hover:bg-transparent",
      "hover:opacity-90",
    ],
  },
  variants: {
    color: {
      neutral: { base: "border-tag-neutral bg-tag-neutral" },
      accent: { base: "border-tag-blue bg-tag-blue" },
      warning: { base: "border-tag-yellow bg-tag-yellow" },
      success: { base: "border-tag-green bg-tag-green" },
      danger: { base: "border-tag-red bg-tag-red" },
      purple: { base: "border-tag-purple bg-tag-purple" },
    },
    size: {
      sm: { base: "h-6 rounded-sm px-1" },
      md: { base: "h-7 rounded-sm px-2", content: "font-semibold" },
      lg: {
        base: "h-8 rounded-md px-2 text-sm",
        content: "font-semibold",
        removeButton: "text-sm",
      },
    },
    hasStartContent: {
      true: {},
      false: {},
    },
    hasEndContent: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        base: "pointer-events-none opacity-60",
      },
    },
  },
  compoundVariants: [
    {
      size: "sm",
      hasEndContent: true,
      className: {
        content: "pr-0",
      },
    },
    {
      size: ["md", "lg"],
      hasEndContent: true,
      className: {
        content: "pr-0.5",
      },
    },
    {
      size: "sm",
      hasStartContent: true,
      className: {
        content: "pl-0",
      },
    },
    {
      size: ["md", "lg"],
      hasStartContent: true,
      className: {
        content: "pl-0.5",
      },
    },
  ],
  defaultVariants: {
    color: "neutral",
    size: "md",
    isDisabled: false,
  },
});

const cloneContent = (content: ReactNode) =>
  isValidElement<HTMLAttributes<HTMLElement>>(content)
    ? cloneElement(content, {
        className: cn("max-h-[80%]", content.props.className),
      })
    : null;

type TagClassNames = SlotsToClasses<keyof ReturnType<typeof tag>>;

export interface TagProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "color">,
    Omit<VariantProps<typeof tag>, "hasEndContent"> {
  startContent?: ReactNode;
  endContent?: ReactNode;
  onRemove?: () => void;
  classNames?: TagClassNames;
}

export const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    {
      className,
      color,
      size,
      isDisabled,
      children,
      startContent: startContentProp,
      endContent: endContentProp,
      onRemove,
      classNames,
      ...props
    },
    ref
  ) => {
    const domRef = useDomRef(ref);

    const isRemovable = !!onRemove;
    const hasStartContent = !!startContentProp;
    const hasEndContent = !!endContentProp || !!onRemove;

    const slots = useMemo(
      () =>
        tag({
          color,
          size,
          hasStartContent,
          hasEndContent,
        }),
      [color, size, hasStartContent, hasEndContent]
    );

    const { focusProps } = useFocusRing();
    const { pressProps } = usePress({ isDisabled, onPress: onRemove });

    const startContent = cloneContent(startContentProp);

    const endContent = useMemo(() => {
      const content = cloneContent(endContentProp);
      if (isRemovable) {
        return (
          <span
            role="button"
            aria-label="close tag"
            tabIndex={0}
            className={slots.removeButton({ className: classNames?.removeButton })}
            {...mergeProps(focusProps, pressProps)}
          >
            {content || <FontAwesomeIcon fixedWidth icon={faXmark} />}
          </span>
        );
      }
      return content;
    }, [endContentProp, isRemovable, slots, focusProps, pressProps, classNames?.removeButton]);

    return (
      <div ref={domRef} className={slots.base({ className })} {...props}>
        {startContent}
        <span className={slots.content({ className: classNames?.content })}>{children}</span>
        {endContent}
      </div>
    );
  }
);
Tag.displayName = "Tag";
